import { Fragment, useState } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import { EmptyIndicator, Link } from 'components/common';
import { Box } from 'components/common/box';
import { ChaptersDropdownFilter, TaskSummary } from 'components/common/tasks';

import type { Chapter, Task } from 'shared-types';

interface Props {
  chapters: (Chapter & { number: number })[];
  courseSlug: string;
  loading: boolean;
  tasks: Task[];
  title: string;
}

const FollowTasksList = ({
  chapters,
  courseSlug,
  loading = false,
  tasks,
  title
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme } = useReduxState();

  const [selectedChapterId, setSelectedChapterId] = useState<number>(null);

  const handleChapterChange = (selectedChapterId: number) =>
    setSelectedChapterId(selectedChapterId);

  const filteredTasks = tasks.length
    ? tasks.filter(
        (task) => !selectedChapterId || selectedChapterId === task.chapter.id
      )
    : null;

  return (
    <div className="tasks">
      <Box
        headerContent={
          <div className="chapters-dropdown-filter">
            <ChaptersDropdownFilter
              chapters={chapters}
              onChange={handleChapterChange}
            />
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={15}
        title={title}
      >
        {tasks?.length ? (
          <div className="tasks-content">
            {filteredTasks?.length ? (
              filteredTasks.map((task, i) => (
                <Fragment key={task.id}>
                  {i === 0 ||
                  task.chapter.number !==
                    filteredTasks[i - 1].chapter.number ? (
                    <div className="chapter-title">
                      {`${task.chapter.number}. ${task.chapter.title}`}

                      {!task.chapter.publishDate ? (
                        <div className="chapter-title-status">
                          {`(${messages.commonNotPublished})`}
                        </div>
                      ) : Date.parse(task.chapter.publishDate) > dateNow ? (
                        <div className="chapter-title-status">
                          {`(${messages.commonWillPublish} ${getFormattedDate({
                            date: task.chapter.publishDate
                          })})`}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="task-wrapper">
                    <Link
                      as={`/admin/courses/${courseSlug}/follow/tasks/${task.id}`}
                      href="/admin/courses/[courseSlug]/follow/tasks/[taskId]"
                      style={{ textDecoration: 'none' }}
                    >
                      <div className="task-link">
                        <TaskSummary task={task} courseSlug={courseSlug} />
                      </div>
                    </Link>
                  </div>
                </Fragment>
              ))
            ) : (
              <EmptyIndicator icon="list-alt" title={messages.tasksNoTasks} />
            )}
          </div>
        ) : (
          <EmptyIndicator
            description={messages.tasksNoTasksDescription}
            icon="list-alt"
            title={messages.tasksNoTasks}
          />
        )}
      </Box>

      <style jsx>{`
        .chapter-title {
          color: ${theme.textColorLight};
          font-size: 1.8rem;
          font-weight: 700;
          margin: 60px 0 20px;
          padding: 0 5px;
        }
        .chapter-title:first-child {
          margin-top: 10px;
        }
        .chapter-title-status {
          color: ${theme.textColorLighter};
          display: inline;
          font-size: 1.4rem;
          font-weight: 400;
          margin-left: 7px;
          position: relative;
          top: -1px;
        }
        .task-wrapper + .task-wrapper {
          margin-top: 40px;
        }
        .task-link {
          border-radius: ${theme.cardBorderRadius};
          color: inherit;
          display: block;
          font-weight: 400;
          text-decoration: none;
          transition: box-shadow 0.3s;
        }
        .task-link:focus,
        .task-link:hover {
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
        }
        @media (min-width: ${breakpoints.small}px) {
          .tasks-content {
            margin: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default FollowTasksList;
